import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Phone, Mail, CalendarDays, Sparkles } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import GalleryManager from "@/components/GalleryManager";
import ChangePassword from "@/components/ChangePassword";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fd_admin_token";

const styleLabels = {
  "all-white-wrap": "All-White Wrap",
  "mirror-gold-silver": "Mirror Gold/Silver Wrap",
  "printed-wrap": "Printed Wrap",
  "single-color": "Single Color Wrap",
  "custom-logo": "Custom Logo Only",
  "white-starlit": "White Starlit LED",
  "black-starlit": "Black Starlit LED",
  "infinity-led-mirror": "Infinity! LED Mirror",
  "event-branding": "Full Event Branding (V-Graphics)",
  other: "Something Else",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leads, setLeads] = useState(null);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLeads(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLeads(null);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center px-6">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm bg-ivory/5 border border-ivory/15 backdrop-blur-xl rounded-[2rem] p-10 text-center">
          <img src="/assets/logo.png" alt="Fantasy Dancefloors" className="h-12 mx-auto mb-6" />
          <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-2">Private</p>
          <h1 className="font-serif text-3xl text-ivory mb-8">The Guest List</h1>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-ivory/25 focus:border-blush outline-none py-3 text-ivory text-center tracking-widest placeholder:text-ivory/30 transition-colors duration-300 mb-6"
          />
          {error && <p data-testid="admin-login-error" className="text-coral text-sm mb-4">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-blush text-midnight text-[12px] tracking-[0.25em] uppercase font-medium py-3.5 hover:bg-gold transition-colors duration-300 disabled:opacity-50"
          >
            {busy ? "Opening…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-midnight px-6 sm:px-10 py-14">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-2">Fantasy Dancefloors · Private</p>
            <h1 className="font-serif text-4xl text-ivory">
              {tab === "enquiries" ? (
                <>Enquiries {leads && <span className="text-ivory/40 text-2xl">({leads.length})</span>}</>
              ) : tab === "gallery" ? (
                <>Gallery</>
              ) : (
                <>Security</>
              )}
            </h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-ivory/60 hover:text-blush transition-colors duration-300"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="flex gap-3 mb-12">
          {[
            { id: "enquiries", label: "Enquiries" },
            { id: "gallery", label: "Gallery" },
            { id: "security", label: "Security" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-6 py-2.5 rounded-full text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 ${
                tab === t.id ? "bg-blush text-midnight" : "text-ivory/60 border border-ivory/20 hover:text-ivory"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "gallery" && <GalleryManager token={token} />}
        {tab === "security" && <ChangePassword token={token} />}
        {tab === "enquiries" && (
          <>
            {leads === null && <p className="text-ivory/50">Loading…</p>}
            {leads && leads.length === 0 && (
              <p className="text-ivory/50 font-light">No enquiries yet — they'll appear here the moment one arrives.</p>
            )}

            <div className="space-y-5">
              {leads?.map((l) => (
                <div key={l.id} data-testid="enquiry-row" className="bg-ivory/5 border border-ivory/10 rounded-2xl p-6 sm:p-8">
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                    <p className="font-serif text-2xl text-ivory">{l.name}</p>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-ivory/40">
                      {new Date(l.timestamp).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-ivory/70 mb-4">
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-blush transition-colors duration-300">
                        <Mail size={13} className="text-gold" /> {l.email}
                      </a>
                    )}
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-blush transition-colors duration-300">
                        <Phone size={13} className="text-gold" /> {l.phone}
                      </a>
                    )}
                    {l.event_date && (
                      <span className="flex items-center gap-2">
                        <CalendarDays size={13} className="text-gold" /> {l.event_date}
                      </span>
                    )}
                    {l.floor_style && (
                      <span className="flex items-center gap-2">
                        <Sparkles size={13} className="text-gold" /> {styleLabels[l.floor_style] || l.floor_style}
                      </span>
                    )}
                  </div>
                  {l.message && <p className="text-ivory/60 font-light text-sm leading-relaxed">{l.message}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
